import {
  BitkitAccordion,
  BitkitAlert,
  BitkitButton,
  BitkitControlButton,
  BitkitDialog,
  BitkitLink,
  BitkitList,
  BitkitNoteCard,
  BitkitSpinner,
  createBitkitToast,
  IconAbortCircle,
  IconArrowNortheast,
  IconCheck,
  IconCross,
  IconInfoCircle,
  IconLock,
  IconSparkleFilled,
  IconThumbDown,
  IconThumbUp,
  rem,
} from '@bitrise/bitkit-v2';
import { Box, BoxProps } from '@chakra-ui/react/box';
import { useDisclosure } from '@chakra-ui/react/hooks';
import { Text } from '@chakra-ui/react/text';
import { keyframes } from '@emotion/react';
import { differenceInDays, differenceInMinutes } from 'date-fns';
import { useContext, useEffect, useState } from 'react';
import { useShallow } from 'zustand/react/shallow';

import useBuildAIAssistantResult, { CreditInfo } from '@/client/BFF/Build/AIAssistantResult/useBuildAIAssistantResult';
import { AIAgentTask } from '@/client/BFF/Workspaces/Projects/AIAgentTasks/types';
import BitriseAiCard from '@/components/BitriseAiCard/BitriseAiCard';
import CopiableInput from '@/components/CopiableInput/CopiableInput';
import ConfirmationDialog from '@/components/Dialog/ConfirmationDialog/ConfirmationDialog';
import MarkdownBlock from '@/components/MarkdownBlock';
import { useMultiTurnFixer } from '@/components/MultiTurnSidebar/MultiTurnFixer';
import { GlobalPropsContext } from '@/components/page/GlobalPropsContext';
import URLs from '@/enums/urls';
import { segmentTrack } from '@/hooks/analytics/useTracking';
import useMonolithApiMutation from '@/hooks/api/useMonolithApiMutation';
import useMonolithApiQuery from '@/hooks/api/useMonolithApiQuery';
import usePublicApiQuery from '@/hooks/api/usePublicApiQuery';
import { AIRunType, Build } from '@/models/Build';
import BuildStatus from '@/models/BuildStatus';
import { SimpleErrorResponse } from '@/models/Error';
import { PublicApiPagedResponse } from '@/models/network';
import { formatDate } from '@/utils/commonFormats';
import {
  abortBuildPath,
  buildDetailsPagePath,
  getAIFixerResultPath,
  getWorkspaceTeamPath,
} from '@/utils/monolithApiRouteService';
import { appPipelinesPath } from '@/utils/publicApiRouteService';

import useBuildDetailsPageStore from '../../BuildDetailsPage.store';
import useBuildDetails from '../../hooks/useBuildDetails';
import buildFailurePrompt from './buildFailurePrompt';
import CopyAsPromptButton from './CopyAsPromptButton';
import useFirstTimeSeeingSummary from './useFirstTimeSeeingSummary';

type AgentSummaryResponse = {
  description: string;
  commitHash: string;
  commitUrl: string;
};

const CreditEmptyDialogSupport = ({
  isOpen,
  onClose,
  workspaceSlug,
  creditInfo,
}: {
  isOpen: boolean;
  onClose: () => void;
  workspaceSlug: string;
  creditInfo: CreditInfo;
}) => {
  return (
    <BitkitDialog
      open={isOpen}
      title="Your workspace used all Bitrise AI credits"
      onOpenChange={({ open }) => !open && onClose()}
    >
      <Box>
        <Text marginBlockEnd="8">Build Fixer requires AI credits.</Text>
        {creditInfo.canUpgrade.permission ? (
          <Text>
            You’ve used all {creditInfo.total} credits for this Bitrise AI billing period.{' '}
            <BitkitLink colorVariant="purple" href={URLs.CONTACT_SUPPORT} isExternal>
              Contact support
            </BitkitLink>{' '}
            for additional AI credits
          </Text>
        ) : (
          <Text>
            You’ve used all {creditInfo.total} credits for this Bitrise AI billing period. To get more, ask one of your{' '}
            <BitkitLink
              colorVariant="purple"
              href={getWorkspaceTeamPath(workspaceSlug, { params: { tab: 'owners' } })}
              isExternal
            >
              workspace owners
            </BitkitLink>{' '}
            to contact support.
          </Text>
        )}
      </Box>
    </BitkitDialog>
  );
};

const CreditEmptyDialogSelfServe = ({
  isOpen,
  onClose,
  workspaceSlug,
  creditInfo,
}: {
  isOpen: boolean;
  onClose: () => void;
  workspaceSlug: string;
  creditInfo: CreditInfo;
}) => {
  const upgradeLink = `/organization/${workspaceSlug}/credit_subscription/add_on_subscriptions/bitrise-ai`;
  return (
    <BitkitDialog
      footerButtons={
        creditInfo.canUpgrade.permission ? (
          <>
            <BitkitButton variant="secondary" onClick={onClose}>
              Dismiss
            </BitkitButton>
            <BitkitButton href={upgradeLink}>Upgrade addon</BitkitButton>
          </>
        ) : undefined
      }
      footerContent={
        !creditInfo.canUpgrade.permission && (
          <Box display="flex" flexDirection="column" gap="16">
            <Text>
              To upgrade add-on, send the URL below to one of your{' '}
              <BitkitLink
                colorVariant="purple"
                href={getWorkspaceTeamPath(workspaceSlug, { params: { tab: 'owners' } })}
                isExternal
              >
                workspace owners
              </BitkitLink>
              .
            </Text>
            <CopiableInput hideButtonText width="100%" value={upgradeLink} />
          </Box>
        )
      }
      open={isOpen}
      title="Change plan for Bitrise AI add-on to keep using AI features"
      onOpenChange={({ open }) => !open && onClose()}
    >
      <Box>
        <Text marginBlockEnd="8">Build Fixer requires AI credits.</Text>
        <Text>
          You’ve used all {creditInfo.total} credits for this Bitrise AI billing period. Credits reset{' '}
          {formatDate(creditInfo.resetDate)}.
        </Text>
      </Box>
      <Box>
        <Text marginBlockEnd="12">Upgrading Bitrise AI add-on will allow you to speed up the build process:</Text>
        <BitkitList>
          <BitkitList.Item icon={IconCheck} iconColor="icon/positive">
            Get actionable solutions on how to fix failed builds.
          </BitkitList.Item>
          <BitkitList.Item icon={IconCheck} iconColor="icon/positive">
            Auto‑fix failed builds with AI.
          </BitkitList.Item>
          <BitkitList.Item icon={IconCheck} iconColor="icon/positive">
            Receive comments on pull requests.
          </BitkitList.Item>
        </BitkitList>
      </Box>
    </BitkitDialog>
  );
};

const Feedback = ({ appSlug, buildSlug }: { appSlug: string; buildSlug: string }) => {
  const handleFeedback = (reactionType: 'upvote' | 'downvote') => {
    segmentTrack('Build Whisperer Reaction Button Clicked', {
      event_schema_version: 2.0,
      app_slug: appSlug,
      build_slug: buildSlug,
      reaction_type: reactionType,
      is_multi_turn: false,
    });
    setFeedbackSent(true);
  };
  const [feedbackSent, setFeedbackSent] = useState(false);
  return (
    <Box display="flex" flexDirection="row" justifyContent="space-between" alignItems="center">
      {feedbackSent ? (
        <Text textStyle="body/md/semibold">Thank you for your feedback!</Text>
      ) : (
        <>
          <Box display="flex" flexDirection="row" alignItems="center">
            <Text textStyle="body/md/regular" color="text/secondary" verticalAlign="bottom">
              Do you find this useful? Help us improve our AI assistant.
            </Text>
            <BitkitControlButton
              icon={IconThumbUp}
              color="icon/secondary"
              label="Useful"
              marginInlineStart="8"
              onClick={() => handleFeedback('upvote')}
            />
            <BitkitControlButton
              color="icon/secondary"
              icon={IconThumbDown}
              label="Not useful"
              onClick={() => handleFeedback('downvote')}
            />
          </Box>
        </>
      )}
    </Box>
  );
};

const loadingTextLabels = ['Thinking', 'Planning changes', 'Preparing updates', 'Working'];
const LoadingText = ({ singleLabel }: { singleLabel?: string }) => {
  const shimmer = keyframes`
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
`;

  const [textIndex, setTextIndex] = useState(0);

  useEffect(() => {
    if (singleLabel) return;

    const interval = setInterval(() => {
      setTextIndex((prev) => (prev + 1) % loadingTextLabels.length);
    }, 3000);

    return () => clearInterval(interval);
  }, [singleLabel]);

  return (
    <Text
      color="text/secondary"
      textStyle="body/md/regular"
      verticalAlign="bottom"
      bgImage="linear-gradient(90deg, rgba(0, 0, 0, 0) calc(50% - 60px), color-mix(in srgb, var(--colors-background\/primary) 84%, transparent), rgba(0, 0, 0, 0) calc(50% + 60px)), linear-gradient(var(--colors-text\/secondary), var(--colors-text\/secondary))"
      bgSize="200% auto"
      bgClip="text"
      animation={`${shimmer} 3s linear infinite`}
    >
      {singleLabel || loadingTextLabels[textIndex]}...
    </Text>
  );
};

const FailedSummary = ({
  failureReason,
  failureDescription,
  notFixableReason,
}: {
  failureReason: string;
  failureDescription: string;
  notFixableReason: string;
}) => {
  return (
    <Box maxWidth={800}>
      <Text as="h5" textStyle="heading/h5" marginBlockEnd="10px">
        Reason
      </Text>
      <MarkdownBlock md={failureReason} size="md" />
      <Text as="h5" textStyle="heading/h5" marginBlockStart="16" marginBlockEnd="10px">
        Suggested solution
      </Text>
      {failureDescription && !notFixableReason && <MarkdownBlock md={failureDescription} size="md" />}
      {notFixableReason && (
        <BitkitAlert
          variant="warning"
          titleText="Bitrise AI cannot automatically fix this build."
          messageText={notFixableReason}
        />
      )}
    </Box>
  );
};

const SummaryCouldntRunCard = ({ marginBlockEnd }: Pick<BoxProps, 'marginBlockEnd'>) => {
  const [dismissed, setDismissed] = useState(false);
  if (dismissed) return null;
  return (
    <Box
      background="background/primary"
      display="flex"
      border="1px solid"
      borderColor="border/regular"
      borderRadius="8"
      flexDirection="row"
      marginBlockEnd={marginBlockEnd}
    >
      <Box background="background/secondary" borderLeftRadius={rem(7)} paddingInline="8" paddingBlock="12">
        <IconSparkleFilled color="icon/tertiary" size="24" />
      </Box>
      <Box flex="1" paddingInline="12" paddingBlock="12" display="flex" flexDirection="row" alignItems="center">
        <Box flex="1">
          <Text textStyle="body/lg/semibold" color="text/secondary">
            Summary couldn’t run for this build
          </Text>
          <Text textStyle="body/md/regular" color="text/tertiary">
            AI features are paused until you buy more credits or your billing period resets.
          </Text>
        </Box>
        <BitkitControlButton icon={IconCross} label="Dismiss" onClick={() => setDismissed(true)} />
      </Box>
    </Box>
  );
};

const AIAssistantCard = ({ marginBlockEnd }: Pick<BoxProps, 'marginBlockEnd'>) => {
  const {
    featureFlags: { account: accountFeatureFlags },
  } = useContext(GlobalPropsContext);

  const [fixerConfirmDialogOpen, setFixerConfirmDialogOpen] = useState(false);
  const creditEmptyDialog = useDisclosure();
  const { show, lastTurnId, isTaskPath: sidebarIsTaskPath } = useMultiTurnFixer();

  const {
    appSlug,
    appTitle,
    buildSlug,
    buildNumber,
    commitHash,
    tag,
    triggeredWorkflow,
    copyFailureAsPrompt,
    workspaceSlug,
    branch,
    failed,
    finishedAt,
    isSummaryEnabled,
    failureSummaryConfigured,
    aiRunType,
    initialFixerBuildSlug,
    initialTurnId,
  } = useBuildDetailsPageStore(
    useShallow((store) => {
      return {
        initialFixerBuildSlug: store.build.agentRunType === 'build-fixer' ? store.build.agentRunSlug : undefined,
        initialTurnId: store.build.agentRunType === 'build-fixer-multiturn' ? store.build.agentRunSlug : undefined,
        isSummaryEnabled: Boolean(store.failedBuildAiState.options?.summary),
        failureSummaryConfigured: store.build.failureSummaryConfigured ?? null,
        aiRunType: store.build.aiRunType,
        finishedAt: store.build.finishedAt,
        failed: store.build.status === BuildStatus.Error,
        buildSlug: store.build.slug,
        buildNumber: store.build.buildNumber,
        commitHash: store.build.commitHash,
        tag: store.build.tag,
        triggeredWorkflow: store.build.triggeredWorkflow,
        appSlug: store.app.slug,
        appTitle: store.app.title,
        copyFailureAsPrompt: store.copyFailureAsPrompt,
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        workspaceSlug: store.app.owner!.slug,
        branch: store.build.branch,
      };
    }),
  );

  const veryOldBuild = Boolean(finishedAt && differenceInDays(new Date(), finishedAt) > 30);
  const finishedRecently = Boolean(finishedAt && differenceInMinutes(new Date(), finishedAt) < 5);
  const isEnabled = failed && isSummaryEnabled && !aiRunType;

  const { data: summaryData, error: summaryError } = useBuildAIAssistantResult(buildSlug, {
    enabled: isEnabled,
    refetchInterval() {
      return finishedRecently ? 10 * 1000 : undefined;
    },
  });
  useEffect(() => {
    if (initialTurnId) {
      show(
        {
          variant: 'multi-turn',
          props: {
            appSlug,
            buildSlug,
            lastTurnId: initialTurnId,
            isTaskPath: accountFeatureFlags?.aiAgentTasksEnabled,
          },
        },
        { immediate: true },
      );
    }
  }, [initialTurnId, appSlug, buildSlug, show, accountFeatureFlags?.aiAgentTasksEnabled]);
  const fixerEnabled = summaryData && summaryData.fixerStatus !== 'disabled';
  const showAIFixer = Boolean(!lastTurnId && fixerEnabled);
  const isFirstVisit = useFirstTimeSeeingSummary(buildSlug, summaryData ? finishedAt : null);
  const [showMoreState, setShowMore] = useState<boolean>();

  const [fixerBuildSlug, setFixerBuildSlug] = useState(initialFixerBuildSlug || '');

  const showMore =
    typeof showMoreState === 'undefined' ? Boolean(fixerBuildSlug) || (isFirstVisit && !veryOldBuild) : showMoreState;

  const [isFixerAborted, setIsFixerAborted] = useState(false);
  const { build: fixerBuild } = useBuildDetails(appSlug, fixerBuildSlug, false, undefined, {
    isEnabled: showAIFixer && !!fixerBuildSlug,
  });

  const agentRunRequest = useMonolithApiMutation<
    Build | AIAgentTask,
    SimpleErrorResponse,
    { buildSlug: string; verboseMode: boolean; aiRunType: AIRunType }
  >(`/apps/${appSlug}/agent_runs`, 'POST', {
    onSuccess: (response, vars) => {
      if (vars.aiRunType === 'build-fixer-multiturn') {
        const isTaskPath = !('slug' in response.data);
        const lastTurnId = isTaskPath ? (response.data as AIAgentTask).id : (response.data as Build).slug;
        show({ variant: 'multi-turn', props: { appSlug, buildSlug, lastTurnId, isTaskPath } });
      } else {
        setFixerBuildSlug((response.data as Build).slug);
      }
      setShowMore(true);
    },
    onError: (err) => {
      createBitkitToast({
        messageText: `${JSON.stringify(err.response?.data)}`,
        titleText: 'Error triggering agent run',
        variant: 'critical',
      });
    },
  });

  const abortFixerBuildRequest = useMonolithApiMutation(abortBuildPath(fixerBuildSlug), 'post', {
    onSuccess: () => {
      setIsFixerAborted(true);
    },
  });

  const {
    data: fixerSummary,
    error: fixerSummaryError,
    isLoading: isFixerSummaryLoading,
  } = useMonolithApiQuery<AgentSummaryResponse, SimpleErrorResponse>(
    ['build', buildSlug, 'ai-fixer-result'],
    getAIFixerResultPath(buildSlug),
    {
      enabled: !!fixerBuildSlug && fixerBuild?.status === BuildStatus.Success,
    },
  );

  const { data: buildListForFixer } = usePublicApiQuery<PublicApiPagedResponse<Build>, SimpleErrorResponse>(
    ['buildListForFixer', buildSlug],
    appPipelinesPath(appSlug, { branch, limit: 10 }),
    {
      enabled: !!fixerSummary?.commitHash,
    },
  );

  const buildTriggeredByFixer = buildListForFixer?.data?.find(
    (build) =>
      build.status === BuildStatus.Success &&
      !build.aiRunType &&
      build.triggerParams?.commitHash === fixerSummary?.commitHash,
  );

  if (!isEnabled) return null;

  if (
    failureSummaryConfigured &&
    finishedRecently &&
    !summaryData &&
    (!summaryError || summaryError.response?.status === 404)
  ) {
    return (
      <BitkitNoteCard
        role="progressbar"
        status="progress"
        message="AI summary in progress..."
        marginBlockEnd={marginBlockEnd}
      />
    );
  }
  if (!summaryData) {
    if (failureSummaryConfigured === false) {
      return <SummaryCouldntRunCard marginBlockEnd={marginBlockEnd} />;
    }
    return null;
  }

  const fixerStatus = (() => {
    if (fixerSummary) {
      return 'completed';
    }

    if (isFixerAborted || fixerBuild?.status === BuildStatus.Aborted) {
      return 'aborted';
    }

    if (fixerBuild?.status === BuildStatus.Running) {
      return 'running';
    }

    if (fixerBuild?.status === BuildStatus.Error || fixerSummaryError) {
      return 'failed';
    }

    if (
      (agentRunRequest.isPending && agentRunRequest.variables.aiRunType === 'build-fixer') ||
      fixerBuildSlug ||
      isFixerSummaryLoading
    ) {
      return 'loading';
    }

    return null;
  })();

  const onFixClick = () => {
    setFixerConfirmDialogOpen(false);
    setIsFixerAborted(false);
    agentRunRequest.mutate({ buildSlug, verboseMode: false, aiRunType: 'build-fixer' });
  };

  const emptyDialog = () => {
    if (summaryData.fixerStatus !== 'out-of-credits') return undefined;
    if (summaryData.creditInfo.canUpgrade.selfServe) {
      return (
        <CreditEmptyDialogSelfServe
          isOpen={creditEmptyDialog.open}
          onClose={creditEmptyDialog.onClose}
          workspaceSlug={workspaceSlug}
          creditInfo={summaryData.creditInfo}
        />
      );
    }

    return (
      <CreditEmptyDialogSupport
        isOpen={creditEmptyDialog.open}
        onClose={creditEmptyDialog.onClose}
        workspaceSlug={workspaceSlug}
        creditInfo={summaryData.creditInfo}
      />
    );
  };

  const cardChildren = fixerStatus ? (
    <BitkitAccordion defaultValue={['failure-details']} borderBottom="0" marginBlockEnd="12">
      <BitkitAccordion.Item value="failure-details">
        <BitkitAccordion.ItemTrigger>Failure details</BitkitAccordion.ItemTrigger>
        <BitkitAccordion.ItemBody>
          <FailedSummary
            failureReason={summaryData.failureInfo.failureReason}
            failureDescription={summaryData.failureInfo.failureDescription}
            notFixableReason={summaryData.failureInfo.notFixableByAgentReason}
          />
          <Box width="100%" marginBlockStart="16" paddingInline="16">
            <Feedback appSlug={appSlug} buildSlug={buildSlug} />
          </Box>
        </BitkitAccordion.ItemBody>
      </BitkitAccordion.Item>
      <BitkitAccordion.Item value="fix-details">
        <BitkitAccordion.ItemTrigger>Fix details</BitkitAccordion.ItemTrigger>
        <BitkitAccordion.ItemBody>
          <Box maxWidth={800}>
            {fixerStatus === 'running' && (
              <Box display="flex" alignItems="center" marginBlockEnd={marginBlockEnd}>
                <BitkitSpinner color="icon/interactive" marginInlineEnd="12" width="16" height="16" aria-hidden />
                <LoadingText />
              </Box>
            )}
            {fixerStatus === 'loading' && (
              <Box display="flex" alignItems="center" marginBlockEnd={marginBlockEnd}>
                <BitkitSpinner color="icon/interactive" marginInlineEnd="12" width="16" height="16" aria-hidden />
                <LoadingText singleLabel="Loading" />
              </Box>
            )}

            {fixerStatus === 'aborted' && (
              <Box textStyle="body/md/regular" color="text/secondary" display="flex" alignItems="center" gap="8">
                <IconInfoCircle color="icon/secondary" size="16" /> Build fixer aborted.
              </Box>
            )}

            {fixerStatus === 'completed' && (
              <Box textStyle="body/md/regular">
                The AI fixer build has completed. Please check the build details for more information.
                <Text as="h5" textStyle="heading/h5" marginBlock="12">
                  Results
                </Text>
                <Text>{fixerSummary?.description}</Text>
                <BitkitList marginBlockStart="12">
                  <BitkitList.Item>
                    Pushed changes to GitHub:&nbsp;
                    <BitkitLink colorVariant="purple" href={fixerSummary?.commitUrl} isExternal>
                      {fixerSummary?.commitHash}
                    </BitkitLink>
                  </BitkitList.Item>
                  {buildTriggeredByFixer && (
                    <BitkitList.Item>
                      Triggered build:&nbsp;
                      <BitkitLink colorVariant="purple" href={`/build/${buildTriggeredByFixer.slug}`} isExternal>
                        Build #{buildTriggeredByFixer.buildNumber}
                      </BitkitLink>
                    </BitkitList.Item>
                  )}
                  <BitkitList.Item>
                    Agent logs:&nbsp;
                    <BitkitLink colorVariant="purple" href={`/build/${fixerBuildSlug}`} isExternal>
                      view run logs
                    </BitkitLink>
                  </BitkitList.Item>
                </BitkitList>
                <Box width="100%" marginBlockStart="16">
                  <Feedback appSlug={appSlug} buildSlug={buildSlug} />
                </Box>
              </Box>
            )}
          </Box>

          {fixerStatus === 'failed' && (
            <Box>
              <BitkitAlert
                variant="critical"
                action={{
                  label: 'Try again',
                  onClick: () => onFixClick(),
                }}
                titleText="Unable to apply fix automatically"
                messageText={
                  <>
                    Bitrise AI couldn’t apply a fix. View the{' '}
                    <BitkitLink href={`/build/${fixerBuildSlug}`} textDecoration="underline" isExternal>
                      agentic logs
                    </BitkitLink>{' '}
                    to see what went wrong.
                  </>
                }
              />
            </Box>
          )}
        </BitkitAccordion.ItemBody>
      </BitkitAccordion.Item>
    </BitkitAccordion>
  ) : (
    <Box borderTop="1px solid" borderColor="border/minimal" paddingBlock="16" paddingInline="12">
      <Text as="h4" textStyle="heading/h4" marginBlockEnd="16">
        Failure details
      </Text>
      <FailedSummary
        failureReason={summaryData.failureInfo.failureReason}
        failureDescription={summaryData.failureInfo.failureDescription}
        notFixableReason={summaryData.failureInfo.notFixableByAgentReason}
      />
      <Box width="100%" marginBlockStart="16">
        <Feedback appSlug={appSlug} buildSlug={buildSlug} />
      </Box>
    </Box>
  );

  const copyAsPromptButton = copyFailureAsPrompt && (
    <CopyAsPromptButton
      getPrompt={() =>
        buildFailurePrompt({
          appTitle,
          appSlug,
          buildSlug,
          buildNumber,
          buildUrl: `${window.location.origin}${buildDetailsPagePath(appSlug, buildSlug)}`,
          branch,
          tag,
          commitHash,
          workflow: triggeredWorkflow,
          failureInfo: summaryData.failureInfo,
        })
      }
      onCopied={() =>
        segmentTrack('Build Whisperer Prompt Copied', {
          app_slug: appSlug,
          build_slug: buildSlug,
          ...(summaryData.failureInfo.stepExecutionId && {
            step_execution_id: summaryData.failureInfo.stepExecutionId,
          }),
          build_fixer_status: summaryData.fixerStatus,
          is_fixable_by_build_fixer: summaryData.failureInfo.isFixableByAgent,
          platform: 'website',
        })
      }
    />
  );

  const cardFooter = (() => {
    if (lastTurnId && fixerEnabled) {
      return (
        <BitkitButton
          size="md"
          variant="secondary"
          onClick={() => {
            show({
              variant: 'multi-turn',
              props: { appSlug, buildSlug, lastTurnId, isTaskPath: sidebarIsTaskPath },
            });
          }}
        >
          Open AI Assistant
        </BitkitButton>
      );
    }

    if (showAIFixer && !fixerSummary) {
      return (
        <Box display="flex" flexWrap="wrap" alignItems="center" gap="16">
          {(!fixerStatus || fixerStatus === 'loading' || fixerStatus === 'failed' || fixerStatus === 'aborted') && (
            <BitkitButton
              disabledReason="Bitrise AI cannot automatically fix this build."
              size="md"
              variant={copyFailureAsPrompt ? 'ai-primary' : 'ai-secondary'}
              icon={copyFailureAsPrompt ? undefined : IconSparkleFilled}
              state={
                agentRunRequest.isPending || fixerStatus === 'loading'
                  ? 'loading'
                  : !summaryData.failureInfo.isFixableByAgent
                    ? 'disabled'
                    : undefined
              }
              onClick={() => {
                if (summaryData.fixerStatus === 'out-of-credits') {
                  creditEmptyDialog.onOpen();
                } else {
                  setFixerConfirmDialogOpen(true);
                }
              }}
              suffixIcon={summaryData.fixerStatus === 'out-of-credits' ? IconLock : undefined}
            >
              Fix with AI
            </BitkitButton>
          )}
          {fixerStatus !== 'running' && copyAsPromptButton}
          {fixerStatus === 'running' && (
            <>
              <BitkitButton
                size="md"
                variant="danger-secondary"
                icon={IconAbortCircle}
                onClick={() => {
                  abortFixerBuildRequest.mutate({
                    abort_reason: 'ai_agent_aborted_by_user',
                    abort_with_success: false,
                    skip_notifications: true,
                  });
                }}
              >
                Abort
              </BitkitButton>
              {fixerBuildSlug && (
                <BitkitButton
                  href={buildDetailsPagePath(appSlug, fixerBuildSlug)}
                  size="md"
                  variant="secondary"
                  suffixIcon={IconArrowNortheast}
                  target="_blank"
                >
                  Follow logs
                </BitkitButton>
              )}
            </>
          )}
        </Box>
      );
    }

    if (!fixerEnabled && copyAsPromptButton) {
      return copyAsPromptButton;
    }

    return undefined;
  })();

  return (
    <>
      <BitriseAiCard
        title="Bitrise AI"
        subtitle="View details for a concise summary and recommendations."
        isOpen={showMore}
        onToggle={() => setShowMore(!showMore)}
        marginBlockEnd={marginBlockEnd}
        footer={cardFooter}
      >
        {cardChildren}
      </BitriseAiCard>
      <ConfirmationDialog
        isVisible={fixerConfirmDialogOpen}
        onClose={() => setFixerConfirmDialogOpen(false)}
        title="Fix build with AI"
        text={
          <>
            <Box textStyle="body/md/regular">
              Bitrise AI will:
              <BitkitList marginBlockStart="12">
                <BitkitList.Item>Start an agent to correct the failed build.</BitkitList.Item>
                <BitkitList.Item>
                  Push changes to current branch. This push will trigger the same CI checks you’ve already configured
                  for your project.
                </BitkitList.Item>
              </BitkitList>
              <BitkitNoteCard
                status="info"
                message={
                  <>
                    These actions require write access to your repository. &nbsp;
                    <BitkitLink
                      colorVariant="purple"
                      href="https://docs.bitrise.io/en/bitrise-platform/integrations/github-app-integration.html"
                      isExternal
                    >
                      Learn more
                    </BitkitLink>
                  </>
                }
                marginBlockStart="24"
              />
            </Box>
          </>
        }
        confirmText="Continue with fix"
        closeText="Cancel"
        isError={false}
        onConfirm={() => onFixClick()}
        isDanger={false}
        isInProgress={false}
      />
      {emptyDialog()}
    </>
  );
};

export default AIAssistantCard;
